import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';

import { BackgroundTaskType } from 'src/services/system/system.types';
import { MailerTargetType, EmailMessageTemplateType } from 'src/services/email-sync/email-sync.types';

export type StackedRecord = { type: 'deal' | 'contact' | 'company'; id: string };

/** JT1-1827: a call just placed from a profile, list row or quick view, waiting for its outcome. */
export type CallOutcomePrompt = {
  personType: 'contact' | 'candidate';
  personId: string;
  name?: string;
  /** When the call was placed, in epoch ms, for the call timer and duration. */
  startedAt: number;
  dialledNumber?: string;
};

export interface IAppState {
  maintenanceMode: boolean;
  maintenanceMessage?: string;
  documentViewer: {
    open: boolean;
    title?: string;
    fileUrl?: string;
  };

  candidateViewer: {
    open: boolean;
    candidateId?: string;
  };

  /**
   * JT1-1736: a record opened on top of the one in view — a linked company or contact from a deal
   * overlay, or a linked deal from a contact / company profile. One slot, so layers never pile up.
   */
  stackedRecord: StackedRecord | null;

  /** JT1-1827: opens the call outcome overlay when the person is on one of the caller's call lists. */
  callOutcomePrompt: CallOutcomePrompt | null;

  calendarEventViewer: {
    open: boolean;
    eventId?: string;
  };

  singleMailerDialog: {
    open: boolean;
    minimized: boolean;
    templateType?: EmailMessageTemplateType;
    templateEntityId?: string;
    emails?: string[];
    targetType?: MailerTargetType;
    targetId?: string;
    /** A drafted email to open with, e.g. an AI outreach draft (JT1-1818). `body` is HTML. */
    title?: string;
    subject?: string;
    body?: string;
  };

  multiMailerDialog: {
    open: boolean;
    minimized: boolean;
    templateType?: EmailMessageTemplateType;
    entities?: string[];
    targetType?: MailerTargetType;
    listId?: string
  };

  taskSidesheet: {
    open: boolean;
    taskId?: string | null;
    dealId?: string | null;
    companyId?: string | null;
    contactId?: string | null;
    jobId?: string | null;
    applicationId?: string | null;
    candidateId?: string | null;
  };

  taskViewer: {
    open: boolean;
    taskId?: string | null;
  };

  accountViewer: {
    open: boolean;
    accountId?: string;
  };

  userFeedbackDialog: {
    open: boolean;
  };

  background_task_state: {
    task_type?: BackgroundTaskType;
    total_count: number;
    pending_count: number;
    completed_count: number;
    failed_count: number;
  };
}

const initialState: IAppState = {
  maintenanceMode: false,
  maintenanceMessage: undefined,
  documentViewer: {
    open: false,
    fileUrl: undefined,
  },
  taskSidesheet: {
    open: false,
    taskId: null,
    dealId: null,
    companyId: null,
    contactId: null,
  },
  accountViewer: {
    open: false,
    accountId: undefined,
  },
  calendarEventViewer: {
    open: false,
    eventId: undefined,
  },
  singleMailerDialog: {
    open: false,
    minimized: false,
    emails: [],
  },
  multiMailerDialog: {
    open: false,
    minimized: false,
    entities: [],
  },
  taskViewer: {
    open: false,
    taskId: null,
  },
  userFeedbackDialog: {
    open: false,
  },
  candidateViewer: {
    open: false,
    candidateId: undefined,
  },
  stackedRecord: null,
  callOutcomePrompt: null,
  background_task_state: {
    task_type: undefined,
    total_count: 0,
    pending_count: 0,
    completed_count: 0,
    failed_count: 0
  }
};

export const appSlice = createSlice({
  name: 'app',
  initialState,
  reducers: {
    setMaintenanceMode: (state, action: PayloadAction<{ mode_state: boolean, message?: string }>) => {
      state.maintenanceMode = action.payload.mode_state;
      state.maintenanceMessage = action.payload.message;
    },

    openDocumentViewer: (state, action: PayloadAction<{ title: string, fileUrl: string }>) => {
      state.documentViewer.open = true;
      state.documentViewer.title = action.payload.title;
      state.documentViewer.fileUrl = action.payload.fileUrl;
    },
    closeDocumentViewer: (state) => {
      state.documentViewer.open = false;
      state.documentViewer.fileUrl = undefined;
      state.documentViewer.title = undefined;
    },

    openCandidateViewer: (state, action: PayloadAction<{ candidateId: string }>) => {
      state.candidateViewer.open = true;
      state.candidateViewer.candidateId = action.payload.candidateId;
    },
    closeCandidateViewer: (state) => {
      state.candidateViewer.open = false;
      state.candidateViewer.candidateId = undefined;
    },

    openStackedRecord: (state, action: PayloadAction<StackedRecord>) => {
      state.stackedRecord = action.payload;
    },
    closeStackedRecord: (state) => {
      state.stackedRecord = null;
    },

    openCallOutcome: (state, action: PayloadAction<CallOutcomePrompt>) => {
      state.callOutcomePrompt = action.payload;
    },
    closeCallOutcome: (state) => {
      state.callOutcomePrompt = null;
    },

    openAccountViewer: (state, action: PayloadAction<{ accountId: string }>) => {
      state.accountViewer.open = true;
      state.accountViewer.accountId = action.payload.accountId;
    },
    closeAccountViewer: (state) => {
      state.accountViewer.open = false;
      state.accountViewer.accountId = undefined;
    },

    openCalendarEventViewer: (state, action: PayloadAction<{ eventId: string }>) => {
      state.calendarEventViewer.open = true;
      state.calendarEventViewer.eventId = action.payload.eventId;
    },
    closeCalendarEventViewer: (state) => {
      state.calendarEventViewer.open = false;
      state.calendarEventViewer.eventId = undefined;
    },

    openTaskEditorSidesheet: (state, action: PayloadAction<{
      taskId?: string | null,
      dealId?: string,
      companyId?: string,
      contactId?: string,
      jobId?: string | null,
      applicationId?: string | null,
      candidateId?: string | null,
    }>) => {
      state.taskSidesheet.open = true;
      state.taskSidesheet.taskId = action.payload.taskId || null;
      state.taskSidesheet.dealId = action.payload.dealId || null;
      state.taskSidesheet.companyId = action.payload.companyId || null;
      state.taskSidesheet.contactId = action.payload.contactId || null;
      state.taskSidesheet.jobId = action.payload.jobId || null;
      state.taskSidesheet.applicationId = action.payload.applicationId || null;
      state.taskSidesheet.candidateId = action.payload.candidateId || null;
    },
    closeTaskEditorSidesheet: (state) => {
      state.taskSidesheet.open = false;
      state.taskSidesheet.taskId = null;
      state.taskSidesheet.dealId = null;
      state.taskSidesheet.companyId = null;
      state.taskSidesheet.contactId = null;
      state.taskSidesheet.jobId = null;
      state.taskSidesheet.applicationId = null;
      state.taskSidesheet.candidateId = null;
    },

    openTaskViewer: (state, action: PayloadAction<{ taskId?: string | null }>) => {
      state.taskViewer.open = true;
      state.taskViewer.taskId = action.payload.taskId || null;
    },
    closeTaskViewer: (state) => {
      state.taskViewer.open = false;
      state.taskViewer.taskId = null;
    },

    openUserFeedbackDialog: (state) => {
      state.userFeedbackDialog.open = true;
    },
    closeUserFeedbackDialog: (state) => {
      state.userFeedbackDialog.open = false;
    },

    openSingleMailerDialog: (state, action: PayloadAction<{ emails?: string[], templateType?: EmailMessageTemplateType, templateEntityId?: string, targetType?: MailerTargetType, targetId?: string, title?: string, subject?: string, body?: string }>) => {
      state.singleMailerDialog.open = true;
      state.singleMailerDialog.minimized = false;
      state.singleMailerDialog.emails = action.payload.emails || [];
      state.singleMailerDialog.templateType = action.payload.templateType;
      state.singleMailerDialog.templateEntityId = action.payload.templateEntityId;
      state.singleMailerDialog.title = action.payload.title;
      state.singleMailerDialog.subject = action.payload.subject;
      state.singleMailerDialog.body = action.payload.body;

      if (action.payload.targetId && action.payload.targetType) {
        state.singleMailerDialog.targetType = action.payload.targetType;
        state.singleMailerDialog.targetId = action.payload.targetId;
      }
    },
    closeSingleMailerDialog: (state) => {
      state.singleMailerDialog.open = false;
      state.singleMailerDialog.minimized = false;
      state.singleMailerDialog.emails = [];
      state.singleMailerDialog.templateType = undefined;
      state.singleMailerDialog.templateEntityId = undefined;
      state.singleMailerDialog.targetType = undefined;
      state.singleMailerDialog.targetId = undefined;
      state.singleMailerDialog.title = undefined;
      state.singleMailerDialog.subject = undefined;
      state.singleMailerDialog.body = undefined;
    },
    minimizeSingleMailerDialog: (state) => {
      state.singleMailerDialog.minimized = true;
    },
    restoreSingleMailerDialog: (state) => {
      state.singleMailerDialog.minimized = false;
    },

    openMultiMailerDialog: (state, action: PayloadAction<{ entities?: string[], templateType?: EmailMessageTemplateType, targetType?: MailerTargetType, listId?: string }>) => {
      if (Array.isArray(action.payload.entities) && action.payload.entities.length > 0 || action.payload.listId) {
        state.multiMailerDialog.open = true;
        state.multiMailerDialog.minimized = false;
        state.multiMailerDialog.entities = action.payload.entities || [];
        state.multiMailerDialog.templateType = action.payload.templateType;
        state.multiMailerDialog.targetType = action.payload.targetType;
        state.multiMailerDialog.listId = action.payload.listId;
      }
    },
    closeMultiMailerDialog: (state) => {
      state.multiMailerDialog.open = false;
      state.multiMailerDialog.minimized = false;
      state.multiMailerDialog.entities = [];
      state.multiMailerDialog.templateType = undefined;
      state.multiMailerDialog.targetType = undefined;
      state.multiMailerDialog.listId = undefined;
    },
    minimizeMultiMailerDialog: (state) => {
      state.multiMailerDialog.minimized = true;
    },
    restoreMultiMailerDialog: (state) => {
      state.multiMailerDialog.minimized = false;
    },

    updateBackgroundTaskState: (state, action: PayloadAction<{ task_type: BackgroundTaskType, total_count: number, pending_count: number, completed_count: number, failed_count: number }>) => {
      state.background_task_state = action.payload;
    },
    resetBackgroundTaskState: (state) => {
      state.background_task_state = {
        task_type: undefined,
        total_count: 0,
        pending_count: 0,
        completed_count: 0,
        failed_count: 0,
      };
    },
  },
});

export const {
  setMaintenanceMode,

  openDocumentViewer,
  closeDocumentViewer,

  openCandidateViewer,
  closeCandidateViewer,

  openStackedRecord,
  closeStackedRecord,

  openCallOutcome,
  closeCallOutcome,

  openAccountViewer,
  closeAccountViewer,

  openTaskEditorSidesheet,
  closeTaskEditorSidesheet,

  openCalendarEventViewer,
  closeCalendarEventViewer,

  openSingleMailerDialog,
  closeSingleMailerDialog,
  minimizeSingleMailerDialog,
  restoreSingleMailerDialog,

  openMultiMailerDialog,
  closeMultiMailerDialog,
  minimizeMultiMailerDialog,
  restoreMultiMailerDialog,

  openTaskViewer,
  closeTaskViewer,

  openUserFeedbackDialog,
  closeUserFeedbackDialog,

  updateBackgroundTaskState,
  resetBackgroundTaskState,
} = appSlice.actions;
export const appReducer = appSlice.reducer;
